import { useCallback, useMemo } from 'react'

import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { invalidateSpotApprovalRequestQueries } from '~/hooks/bidding/queryKeys'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useApproveBiddingSpotApprovalRequests } from '~/pages/SpotBidding/hooks/useApproveBiddingSpotApprovalRequests'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import type { BulkApprovalFlow } from '~/pages/SpotBidding/hooks/useBulkApprovalFlow'
import { useBulkApprovalFlow } from '~/pages/SpotBidding/hooks/useBulkApprovalFlow'
import type { SpotApprovalDecision } from '~/pages/SpotBidding/hooks/useSpotApprovalDecision'
import type { BulkFailureMessages } from '~/pages/SpotBidding/models/bulkApproval'
import { normalizeBulkResult } from '~/pages/SpotBidding/models/bulkApproval'
import { buildSpotBulkCases } from '~/pages/SpotBidding/models/bulkApprovalCases'
import { selectRevalidationBids } from '~/pages/SpotBidding/models/spotApprovalDecision'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import type { VolumeCountableBid } from '~/pages/SpotBidding/models/spotBidValidation'
import {
  spotWriteBlockedReasonLabel,
  translateSpotWriteError,
} from '~/pages/SpotBidding/models/spotBidWrite'

type Params = {
  /** PENDING の申請セット群 (画面の並び順。 `useSpotApprovalDecision` に渡すものと同じ)。 */
  pendingRequests: BiddingSpotApprovalRequest[]
  /** 受渡日内の全札。 */
  bids: SpotBidRow[]
  /** 上限数量(コマ計) の再検証母集合 (`useSpotApprovalDecision.volumeTotalRows` と同じ)。 */
  volumeTotalRows: readonly VolumeCountableBid[]
  isBlockVolumeUnresolved: boolean
  /** 単件の承認と共通の可否 policy (締切・確定済みワークフロー・明細未解決)。 */
  decision: Pick<
    SpotApprovalDecision,
    'decisionDisabledReason' | 'decisionBlockedReasonAt'
  >
  targetDate: Date
  /** 承認できた申請セットごとに呼ぶ (個別承認の `onDecided` と同じ callback)。 */
  onDecided?: (requestId: string) => void
}

/**
 * 通常入札タブのまとめて承認 (AC-BSP-118)。 申請セットを案件に写し、 単件の承認確認と同じ
 * 事前チェック・承認者の識別を案件ごとに当てる。 手順は `useBulkApprovalFlow` が持つ。
 */
export const useSpotBulkApproval = ({
  pendingRequests,
  bids,
  volumeTotalRows,
  isBlockVolumeUnresolved,
  decision,
  targetDate,
  onDecided,
}: Params): BulkApprovalFlow => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync } = useApproveBiddingSpotApprovalRequests()
  const approverIdentity = useApproverIdentity()
  // 承認時点の設定値で判定するため、 モーダルを開く時に取り直す (ブロック入札の
  // useBlockBulkApproval と同じ)。 取り直し中も判定に使えない (= 取り直す前の値で判定しない)。
  const {
    limits,
    isFetching: isLimitsFetching,
    isError: isLimitsError,
    refetch: refetchLimits,
  } = useBidLimits(BidSegment.SPOT_NORMAL)

  // 入口の補足と、 全件が承認者でない時に入口を出さない判断に使う。
  const notApproverCount = useMemo(
    () =>
      pendingRequests.filter((r) => approverIdentity(r).isNotApprover).length,
    [approverIdentity, pendingRequests],
  )

  const buildCases = useCallback(
    (pending: readonly BiddingSpotApprovalRequest[]) =>
      buildSpotBulkCases({
        pendingRequests: pending,
        bids,
        ctx: {
          revalidationBids: selectRevalidationBids(bids),
          volumeTotalRows,
          limits,
          isLimitsUnavailable: isLimitsFetching || isLimitsError,
          isBlockVolumeUnresolved,
        },
        identityOf: approverIdentity,
        t,
      }),
    [
      approverIdentity,
      bids,
      isBlockVolumeUnresolved,
      isLimitsError,
      isLimitsFetching,
      limits,
      t,
      volumeTotalRows,
    ],
  )

  const failureMessages = useMemo<BulkFailureMessages>(
    () => ({
      updatedByOther: t('page:biddingSpot.serverError.aborted'),
      canceledOrDeleted: t('page:biddingSpot.serverError.notFound'),
      gateClosure: t('page:biddingSpot.approvalAction.decisionDeadlinePassed'),
      other: t('page:biddingSpot.serverError.unknown'),
      violationFallback: t(
        'page:biddingSpot.serverError.reason.bidLimitViolation',
      ),
    }),
    [t],
  )

  return useBulkApprovalFlow({
    pending: pendingRequests,
    buildCases,
    notApproverCount,
    send: async (targets) => {
      const response = await mutateAsync({ targets })
      return response.results.map((r) =>
        normalizeBulkResult(r, (reason) =>
          spotWriteBlockedReasonLabel(reason, t),
        ),
      )
    },
    invalidate: () => invalidateSpotApprovalRequestQueries(queryClient),
    decisionDisabledReason: decision.decisionDisabledReason,
    decisionBlockedReasonAt: decision.decisionBlockedReasonAt,
    targetDate,
    failureMessages,
    translateError: (err) => translateSpotWriteError(err, t),
    onOpen: () => {
      void refetchLimits()
    },
    onDecided,
  })
}
